/**
 * Notification sound generator (no external mp3 assets).
 * Uses Web Audio API to synthesise three presets:
 *   - chime : gentle two-tone bell
 *   - ding  : short high beep
 *   - pop   : soft low pop
 *
 * Also handles Web Notifications API permission and firing.
 */
let _audioCtx = null;

function getAudioCtx() {
  try {
    if (!_audioCtx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return null;
      _audioCtx = new AC();
    }
    if (_audioCtx.state === "suspended") _audioCtx.resume().catch(() => {});
    return _audioCtx;
  } catch {
    return null;
  }
}

function playTone(ctx, { freq, dur, type = "sine", startAt = 0, gain = 0.15 }) {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  const t0 = ctx.currentTime + startAt;
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

export const SOUND_OPTIONS = [
  { key: "chime", label: "Chime (soft bell)" },
  { key: "ding", label: "Ding (high beep)" },
  { key: "pop", label: "Pop (low tap)" },
  { key: "none", label: "Silent" },
];

export function playNotificationSound(preset = "chime") {
  if (!preset || preset === "none") return;
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    if (preset === "chime") {
      playTone(ctx, { freq: 880, dur: 0.35, type: "sine", startAt: 0, gain: 0.18 });
      playTone(ctx, { freq: 1320, dur: 0.45, type: "sine", startAt: 0.12, gain: 0.14 });
    } else if (preset === "ding") {
      playTone(ctx, { freq: 1600, dur: 0.18, type: "triangle", startAt: 0, gain: 0.22 });
    } else if (preset === "pop") {
      playTone(ctx, { freq: 320, dur: 0.14, type: "sine", startAt: 0, gain: 0.28 });
    }
  } catch {
    /* ignore */
  }
}

export async function ensureNotificationPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission === "granted" || Notification.permission === "denied") {
    return Notification.permission;
  }
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

export function fireBrowserNotification({ title, body, kind = "info", url = "" }) {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
  if (document.visibilityState === "visible") return; // only when tab hidden
  try {
    const n = new Notification(title || "Relay CRM", {
      body: body || "",
      tag: `relay-${Date.now()}`,
      icon: "/favicon.ico",
      badge: "/favicon.ico",
      silent: false,
    });
    n.onclick = () => {
      window.focus();
      if (url) window.location.assign(url);
      n.close();
    };
  } catch {
    /* ignore */
  }
}
